import { autoDetectRenderer, Container, IRendererOptionsAuto } from "pixi.js";
import { FurnitureData } from "../../../stores/FurnitureStore";
import { Wall } from "./Walls/Wall";
import { Floor } from "./Floor";
import { Serializer } from "../persistence/Serializer";
import { FloorPlanSerializable } from "../persistence/FloorPlanSerializable";
import { Action } from "../actions/Action";
import { useStore } from "../../../stores/EditorStore";
import { Point } from "../../../helpers/Point";
import { showNotification } from "@mantine/notifications";

export class FloorPlan extends Container {
    public zoomEditor:any;
    public worldWidth:number;
    public worldHeight:number;

    private static instance: FloorPlan;

    private floors: Floor[];
    private visibleLabels: boolean = true;
    private serializer: Serializer;
    public furnitureId = 0;   // TODO uuid?
    public windowFurniture: FurnitureData;
    public actions: Action[];
    private totalPrices: number = 0;
    private furnitures:any = null;
    private planImage:any = null;

    public currentFloor = 0;
    private constructor() {
        super();
        this.floors = [];
        this.actions = [];
        this.floors.push(new Floor());
        this.addChild(this.floors[0]);
        this.serializer = new Serializer();
    }
    public static get Instance() {
        return this.instance || (this.instance = new this());
    }

    public get CurrentFloor() {
        return this.currentFloor;
    }

    public set CurrentFloor(floor: number) {
        this.currentFloor = floor;
        useStore.setState({ floor: this.currentFloor })
    }

    public toggleLabels() {
        this.visibleLabels = !this.visibleLabels;
        this.floors[this.currentFloor].setLabelVisibility(this.visibleLabels);
    }

    public changeFloor(by: number) {

        this.removeChild(this.floors[this.currentFloor]);
        let previousFloor = this.currentFloor;
        this.CurrentFloor += by;
        if (this.floors[this.currentFloor] == null) {
            this.floors[this.currentFloor] = new Floor(null, this.floors[previousFloor]);
        }
        this.floors[this.currentFloor].setLabelVisibility(this.visibleLabels);
        this.addChild(this.floors[this.currentFloor])
    }

    public print() {
        let opts: IRendererOptionsAuto = {
            preserveDrawingBuffer: true
        };

        let renderer = autoDetectRenderer(opts);
        const image = renderer.plugins.extract.image(this)
        let popup = window.open();
        popup.document.body.appendChild(image);
        popup.focus();
        popup.print();
    }

    public getPlanImage(){
        let opts: IRendererOptionsAuto = {
            preserveDrawingBuffer: true
        };

        let renderer = autoDetectRenderer(opts);
        this.planImage = renderer.plugins.extract.image(this)

        return this.planImage;
    }

    public save() {
        let floorPlan = this.serializer.serialize(this.floors, this.furnitureId);
        let arrayFloor:any = useStore.getState().floorPlanArray;
        arrayFloor.push(floorPlan);
        if(arrayFloor.length > 15){
            arrayFloor.shift();
        }
        useStore.setState({ floorPlanArray: arrayFloor });
        return arrayFloor;
    }

    public load(planText: string) {
        let plan: FloorPlanSerializable = JSON.parse(planText)
        this.reset();
        for (let floorData of plan.floors) {
            let floor = new Floor(floorData);
            this.floors.push(floor);
        }

        this.furnitureId = plan.furnitureId;
        this.floors[0].getWallNodeSequence().setId(plan.wallNodeId);
        this.addChild(this.floors[this.currentFloor]);
        this.furnitures = this.floors[this.currentFloor].furnitureArray;
        this.setFurnituresPrice();
    }

    // removes current floor
    public removeFloor() {
        if (this.floors.length < 2) {
            showNotification({
                title:"Floor removal not permitted",
                message:"This floor is the only floor in the plan. You cannot have a plan with no floors. Create a new floor before deleting.",
                color:'red'
            })
            return;
        }
        let oldCurrentFloor = this.currentFloor;
        this.changeFloor(-1);
        this.floors[oldCurrentFloor].reset();
        this.floors.splice(oldCurrentFloor, 1);
        this.changeFloor(1);
    }

    // cleans up everything. prepare for new load. TODO Feature multiple floors
    private reset() {

        // remove furniture
        for (let floor of this.floors) {
            floor.reset();
        }

        // remove all floors
        this.floors = [];
        this.currentFloor = 0;
        this.furnitureId = 0;

    }

    public addFurniture(obj: FurnitureData, attachedTo?: Wall, coords?: Point, attachedToLeft?: number, attachedToRight?: number) {
        this.furnitureId += 1;
        this.floors[this.currentFloor].addFurniture(obj, this.furnitureId, attachedTo, coords, attachedToLeft, attachedToRight);
        this.setFurnituresPrice();
    }

    public setFurnituresPrice() {
        let array = this.getFurniture();
        let sumPrice = 0;
        for (let a of array) {
            sumPrice += a[1].data.price;
        }

        this.totalPrices = sumPrice;
    }

    public getTotalPrices() {
        this.setFurnituresPrice();
        return this.totalPrices;
    }

    public getTotalFurnitures() {
        this.setFurnituresPrice();
        return this.furnitures = this.floors[this.currentFloor].furnitureArray;
    }

    public setFurniturePosition(id: number, x: number, y: number, angle?: number) {
        this.floors[this.currentFloor].setFurniturePosition(id, x, y, angle);
    }

    public removeFurniture(id: number) {
        this.floors[this.currentFloor].removeFurniture(id);
        this.setFurnituresPrice();
    }

    public getObject(id: number) {
        return this.floors[this.currentFloor].getObject(id)
    }

    public redrawWalls() {
        this.floors[this.currentFloor].redrawWalls();
    }
    
    public removeWallNode(nodeId: number) {

        this.floors[this.currentFloor].removeWallNode(nodeId)
    }

    public removeWall(wall: Wall) {
        this.floors[this.currentFloor].removeWall(wall);
    }

    public addNodeToWall(wall: Wall, coords: Point) {
        return this.floors[this.currentFloor].addNodeToWall(wall, coords);
    }
    public addNode(leftId: number, rightId: number) {
        return this.floors[this.currentFloor].addNode(leftId, rightId);
    }

    public getWallNodeSeq() {
        return this.floors[this.currentFloor].getWallNodeSequence()
    }

    public getFurniture() {
        return this.floors[this.currentFloor].getFurniture();
    }

    public resetAll() {
        // remove all fornitures
        this.furnitureId = 0;
        this.floors[this.currentFloor].removeAllFurniture();
        this.totalPrices = 0;
        this.furnitures = this.floors[this.currentFloor].furnitureArray;  

        // remove all floor
        /* for (let floor of this.floors) {
            floor.reset();
        } */
    }

    public undoFloorPlan() {
        let arrayFloor:[] = useStore.getState().floorPlanArray;

        if(arrayFloor.length > 1){
            this.load(arrayFloor[arrayFloor.length - 2]);
            arrayFloor.splice(-1);
            useStore.setState({ floorPlanArray: arrayFloor })
        }
    }

    public reloadFloorPlan() {
        let arrayFloor:[] = useStore.getState().floorPlanArray;
        if(arrayFloor.length >= 1){
            this.load(arrayFloor[arrayFloor.length - 1]);
            useStore.setState({ floorPlanArray: arrayFloor })
        }
    }
}
